import { useEffect, useState } from 'react';
import { Phone } from 'lucide-react';
import { toast } from 'sonner';
import { Drawer } from '@/components/common/Drawer';
import { Button } from '@/components/common/Button';
import { Select } from '@/components/forms/Select';
import { cn } from '@/lib/cn';
import {
  registrarContactoCobranza,
  CANALES_CONTACTO,
  RESULTADOS_CONTACTO,
  type CanalContacto,
  type ResultadoContacto,
} from '@/services/api/contactosCobranza';
import { listEdificios, type EdificioRow } from '@/services/api/edificios';

/**
 * Tipo mínimo que necesita el dialog — aceptamos cualquier cosa que tenga
 * estos campos (Administracion completa del drawer, TopMorosoRow del
 * dashboard, etc.). Evita tener que fetchear el admin completo solo para
 * registrar un contacto.
 */
export interface AdminLite {
  id: string;
  codigo: string;
  nombre: string;
  responsable_nombre?: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  empresaId: string;
  admin: AdminLite | null;
  onSaved: () => void;
}

/**
 * Dialog para registrar una gestión de cobranza con el administrador.
 * Usado desde Top Morosos en el dashboard y desde la ficha del admin.
 * El contacto queda en contactos_cobranza y se refleja en el timeline de
 * la ficha del admin en tiempo real vía Realtime.
 */
export function RegistrarContactoDialog({
  open,
  onClose,
  empresaId,
  admin,
  onSaved,
}: Props) {
  const [canal, setCanal] = useState<CanalContacto>('llamada');
  const [resultado, setResultado] = useState<ResultadoContacto>('pendiente');
  const [edificioId, setEdificioId] = useState<string>('');
  const [fechaCompromiso, setFechaCompromiso] = useState<string>('');
  const [nota, setNota] = useState('');
  const [saving, setSaving] = useState(false);
  const [edificios, setEdificios] = useState<EdificioRow[]>([]);

  useEffect(() => {
    if (!open || !admin) return;
    setCanal('llamada');
    setResultado('pendiente');
    setEdificioId('');
    setFechaCompromiso('');
    setNota('');
    // Cargo edificios de esa admin para poder marcar el contexto opcional.
    listEdificios({ empresaId, administracionId: admin.id, pageSize: 200 }).then((r) => {
      if (r.ok) setEdificios(r.data.rows);
    });
  }, [open, admin, empresaId]);

  const necesitaCompromiso = resultado === 'prometio_pagar';

  async function onSubmit() {
    if (!admin) return;
    if (necesitaCompromiso && !fechaCompromiso) {
      toast.error('Elegí la fecha en la que promete pagar.');
      return;
    }
    setSaving(true);
    const res = await registrarContactoCobranza({
      administracionId: admin.id,
      canal,
      resultado,
      edificioId: edificioId || null,
      fechaCompromiso: necesitaCompromiso ? fechaCompromiso : null,
      nota: nota.trim() || undefined,
    });
    setSaving(false);
    if (!res.ok) {
      toast.error(res.error.message);
      return;
    }
    toast.success('Gestión registrada.');
    onSaved();
    onClose();
  }

  if (!admin) return null;

  return (
    <Drawer
      open={open}
      onClose={saving ? () => undefined : onClose}
      width={520}
      kicker="Registrar gestión"
      title={admin.nombre}
      description={
        admin.responsable_nombre
          ? `Cód. ${admin.codigo} · ${admin.responsable_nombre}`
          : `Cód. ${admin.codigo}`
      }
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button leftIcon={<Phone size={15} />} onClick={onSubmit} loading={saving}>
            Registrar
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="Canal">
          <div className="grid grid-cols-5 gap-1.5">
            {CANALES_CONTACTO.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => setCanal(c.value)}
                className={cn(
                  'flex flex-col items-center gap-1 rounded-md border px-2 py-2 text-xs font-semibold transition',
                  canal === c.value
                    ? 'border-blue bg-blue-10 text-blue'
                    : 'border-surface-line text-neutral-muted hover:border-neutral-muted/40 hover:text-neutral-ink',
                )}
              >
                <span className="text-lg leading-none">{c.icono}</span>
                {c.label}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Resultado">
          <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {RESULTADOS_CONTACTO.map((r) => (
              <button
                key={r.value}
                type="button"
                onClick={() => setResultado(r.value)}
                className={cn(
                  'rounded-md border px-3 py-2 text-left text-sm font-semibold transition',
                  resultado === r.value
                    ? r.tone === 'danger'
                      ? 'border-status-danger bg-status-danger/10 text-status-danger'
                      : r.tone === 'warn'
                        ? 'border-orange bg-orange/10 text-orange'
                        : r.tone === 'success'
                          ? 'border-status-success bg-status-success/10 text-status-success'
                          : r.tone === 'info'
                            ? 'border-blue bg-blue-10 text-blue'
                            : 'border-neutral-muted/50 bg-surface-bg text-neutral-ink'
                    : 'border-surface-line text-neutral-muted hover:border-neutral-muted/40 hover:text-neutral-ink',
                )}
              >
                {r.label}
              </button>
            ))}
          </div>
        </Field>

        {necesitaCompromiso && (
          <Field label="Fecha de compromiso">
            <input
              type="date"
              value={fechaCompromiso}
              onChange={(e) => setFechaCompromiso(e.target.value)}
              className="input-premium"
            />
          </Field>
        )}

        <Field label="Edificio (opcional — si la gestión fue sobre uno puntual)">
          <Select value={edificioId} onChange={(e) => setEdificioId(e.target.value)}>
            <option value="">Sobre toda la administración</option>
            {edificios.map((e) => (
              <option key={e.id} value={e.id}>
                {e.codigo} · {e.nombre}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Nota">
          <textarea
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            rows={3}
            placeholder="Ej: hablé con María, dice que cobra el viernes y transfiere."
            className="input-premium min-h-[72px] resize-y"
          />
        </Field>
      </div>
    </Drawer>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-neutral-muted">
        {label}
      </label>
      {children}
    </div>
  );
}
